/**
 * Per-tenant, per-hour rate limits for cheap-but-abusable actions.
 *
 * These are an ANTI-ABUSE valve, NOT a commercial quota — a normal user never
 * trips them. They guard actions that are individually cheap yet spend an
 * external resource on a CACHE MISS (e.g. a Serper name→domain lookup): the
 * cross-tenant cache makes repeats free, but a tenant typing thousands of
 * DISTINCT names would each be a live call. Only the un-cached path is counted.
 *
 * Enforced by `RateLimitService.consume(tenantId, key, ...)` (API), backed by a
 * fixed-window counter row on the `usage-counters` table (SK=`RATE#<key>#<hour>`,
 * self-expiring TTL). To throttle a new feature: add a key here + one
 * `consume()` call at the spend site. Nothing else.
 */
export type RateLimitKey =
  | 'resolve_domain'
  | 'unsubscribe'
  | 'public_api'
  | 'targets_import'
  | 'targets_search'
  | 'targets_sitemap'
  | 'geo_autocomplete'
  | 'place_search'
  | 'place_details'
  /**
   * D57 — shared cap for every path that can START a conversation launch:
   * POST /brands/:brandId/personas, POST …/duplicate, POST …/run and both MCP write tools.
   * A per-TENANT DAILY counter because creation/duplication both stamp nextRunAt = now (i.e.
   * they are launch triggers wearing CRUD names) and the one thing being protected is the
   * engine bill.  Count the attempt BEFORE validation so a 400-causing create still burns a slot.
   */
  | 'conversation_run_now'
  /**
   * Prompt Studio: TypeSafe classification calls (C6 lot A).
   * 500 prompts/hour/tenant; counted in PROMPTS (bulk may classify many at once).
   * failClosed: true — a DDB blip must deny, not silently over-bill TypeSafe.
   */
  | 'prompt_classify'
  /**
   * Prompt Studio: language variant translation (lot D, C6).
   * 500 translations/hour/tenant; failClosed: true.
   */
  | 'prompt_translate'
  /**
   * MCP transcript read — on its own per-token key, separate from `conversation_run_now`.
   * The transcript route returns engine prose that may include third-party personal data (§4.8.4).
   * Its cost to us is small; its value to an exfiltrator is high, so the read cap is distinct
   * from the spend cap and set much lower. Applies to the MCP and public-API transcript endpoints.
   */
  | 'mcp_conversation_transcript'
  /**
   * Prompt Studio Search Console import (lot H): one gsc-reader invoke = one token refresh +
   * one searchAnalytics call. 30/hour/tenant; failClosed: true.
   */
  | 'gsc_import';

export interface RateLimitRule {
  /** Max counted actions per rolling clock-hour window, per tenant. */
  perHour?: number;
  /**
   * Max counted actions per calendar day (UTC), per tenant.
   * Lot 4b: geo_autocomplete uses this to guard the Geoapify 3 000/day tenant share.
   * ⚠ perDay-only rules must branch on the YYYY-MM-DD window, not reuse the perHour path —
   * a perDay-only rule with only perHour defined would pass undefined to DynamoDB and be
   * swallowed by RateLimitService's fail-open. The window branch must land with the key.
   */
  perDay?: number;
  /**
   * When true, a DynamoDB infra error (Throttling, ProvisionedThroughputExceeded, timeout)
   * causes the check to DENY the request rather than pass it through.  Set on keys that gate
   * direct third-party spend (Geoapify, Google Places): a traffic spike is exactly when those
   * keys need to hold, and a spike is also exactly when DynamoDB throttles.
   * Keys without this flag keep the original fail-open behaviour so an infra blip never
   * blocks a legitimate, non-spend action.
   */
  failClosed?: boolean;
}

export const RATE_LIMITS: Record<RateLimitKey, RateLimitRule> = {
  // Serper name→domain resolution (authority sources + competitor suggestions).
  // Cache hits are NOT counted, so 20 distinct new names/hour is generous.
  resolve_domain: { perHour: 20 },
  // Public unsubscribe endpoints (no AuthGuard): a valid token replay costs a
  // DDB read-modify-write, so bound it per tenant even though the outcome is
  // idempotent. Generous — a real tenant never unsubscribes 30x/hour.
  unsubscribe: { perHour: 30 },
  // Public API (/api/public/v1/*). Unlike the two above this is NOT about an
  // external resource — every call is pure DynamoDB — but the API runs in the
  // SAME single Lambda as the app, so an unbounded third-party caller spends
  // concurrency and RCUs the UI needs. 1000/h is far above any reporting or
  // BI-sync pattern and far below what could starve the app.
  public_api: { perHour: 1000 },
  // Cibles large-import valve, shared by BOTH halves of an import (begin + commit) —
  // an import IS begin-then-commit, so one budget is the honest unit and the number
  // here is twice the number of full imports per hour.
  //
  // Raised 2026-10-05 from 20 (= 10 imports/h) to 100 (= 50): the sitemap button is now
  // ADDITIVE (`mode: 'merge'`), so building a catalogue means one import PER SITEMAP FILE
  // instead of one per category, and a retailer site exposes dozens. Safe to be generous
  // because this key gates NO third-party spend (unlike the three failClosed keys below);
  // what it still bounds is one `targets-consolidate` invoke per commit on a function with
  // no reservedConcurrency (D42), which is why it stays bounded rather than removed.
  targets_import: { perHour: 100 },
  // Cibles `?q=` search. Unlike the import valve this is a READ, but every call is
  // one `targets-search` Lambda invoke, and that function is SHARED by every tenant
  // with a fixed reservedConcurrency sized for the BP fan-out (236) — so an
  // unbounded typeahead is a fan-out that starves the audits the concurrency was
  // reserved for. 600/h is ~10/min sustained: far above a human typing, far below a
  // script. Added 2026-09-21 (begin was throttled from the start; search was not).
  targets_search: { perHour: 600 },
  // Cibles sitemap discovery + harvest (SitemapService calls). A single discover can
  // hit Serper on a cache miss; a harvest fetches one full sitemap file.
  // Raised 2026-10-05 from 10 to 60: the dialog spends one unit per discover (once per
  // domain, cached) AND one per harvest/re-analyse, so importing a catalogue sitemap by
  // sitemap tripped the valve after ~9 files. 60/h stays far below abuse — a harvest is
  // our own egress, bounded by HARVEST_BUDGET_MS, and discover is the only Serper path.
  targets_sitemap: { perHour: 60 },
  // Geo autocomplete — per-tenant per-DAY cap (Geoapify 3 000/day platform-wide budget).
  // Raised 2026-09-24 from 200 to 500: onboarding a large franchise requires 100-300+ zone
  // autocomplete calls (one per établissement). 500/day still means only 6 tenants can max
  // out the 3 000/day platform before the platform counter in lambda_autocomplete.py
  // hard-stops everyone — acceptable when fewer than 6 tenants onboard heavy on any given day.
  geo_autocomplete: { perDay: 500, failClosed: true },
  // Google Places Autocomplete — per-tenant per-day cap.
  // Raised 2026-09-24 from 200 to 1 000: ~3 calls per établissement added (typing + picking),
  // so 200/day blocks after 66 establishments — mid-onboarding for any franchise. Google
  // Autocomplete is $2.83/1 000, no shared platform pool; this is an anti-abuse valve only.
  place_search: { perDay: 1000, failClosed: true },
  // Google Places Details — per-tenant per-day cap. Expensive Google SKU ($17/1 000).
  // Raised 2026-09-24 from 200 to 500: 1 call per établissement, 200/day was fine for
  // small brands but blocked a 300-location franchise. Platform counter at 2 000/day is the
  // budget guard; the tenant valve is anti-abuse only.
  place_details: { perDay: 500, failClosed: true },
  // D57 — per-tenant DAILY cap on conversation launch triggers (create, duplicate, run-now).
  // 100/day is far above any plausible human usage (creating 100 personas in one day) and far
  // below what makes the `create → launch → delete → create` loop economically viable as an
  // engine-spend amplifier.  Using `perDay` (not perHour) so the counter aligns with the
  // daily turn-meter (D56) that's the downstream backstop.
  // failClosed: true — a DDB failure on this counter must deny (not pass through), since the
  // counter guards direct engine spend. A traffic spike is exactly when DDB throttles and
  // exactly when the guard needs to hold (#8).
  conversation_run_now: { perDay: 100, failClosed: true },
  // MCP transcript read cap — per-token per-hour. The transcript route is the only endpoint
  // whose read cost to us is small and whose value to an exfiltrator is high (§4.8.4): bulk
  // export via a long-lived token bypasses the session guard that the UI relies on.
  // failClosed: true for the same reason as conversation_run_now.
  mcp_conversation_transcript: { perHour: 20, failClosed: true },
  // Prompt Studio: TypeSafe classify calls (C6, C3).  counted in prompts.
  prompt_classify: { perHour: 500, failClosed: true },
  // Prompt Studio: language variant translations (lot D, C6).
  prompt_translate: { perHour: 500, failClosed: true },
  // Prompt Studio: Search Console imports (lot H). Each spends two Google calls on the
  // connected account's quota; the band imports at most once per open (cached).
  gsc_import: { perHour: 30, failClosed: true },
};

/** Error `code` returned (HTTP 429) when a rate limit is exceeded. */
export const RATE_LIMITED_CODE = 'RATE_LIMITED' as const;

/**
 * Error code returned (HTTP 429) when the generation cooldown is hit
 * (C6: 10 s per-brand cooldown on `regenerate` with `studio: true` and `from-seeds`).
 */
export const GEN_COOLDOWN_CODE = 'GEN_COOLDOWN' as const;
